import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Footer } from '../../../components/footers/Footer';
import { HomeHeader } from '../../../components/new/headers/HomeHeader';
import { getBlogDetail } from '../api/api';
import { getBlogGraphic2Url, getMediaDisplaySrc, resolveContentMedia } from '../helper/graphic2';
import { PageMain } from '../../../components/seo/PageMain';
import { DetailPageHelmet } from '../helper/DetailPageHelmet';
import { getPublicSiteUrl } from '../siteUrl';
import { documentTitle, metaDescriptionFromRecord } from '../helper/detailSeo';

const BLOG_RICH_HTML_CLASS =
  [
    'new-blog-content w-full max-w-none prose prose-base md:prose-lg',
    '[&_form]:mx-auto [&_form]:mt-6 [&_form]:w-full [&_form]:max-w-[760px] [&_form]:rounded-2xl [&_form]:border [&_form]:border-gray-200 [&_form]:bg-white [&_form]:p-6 [&_form]:shadow-[0_12px_30px_rgba(2,23,68,0.06)]',
    '[&_form]:max-md:max-w-full [&_form]:max-md:p-4',
    '[&_form_h2]:mt-0 [&_form_h3]:mt-0 [&_form_p]:mt-0 [&_form_h2]:mb-[0.85rem] [&_form_h3]:mb-[0.85rem] [&_form_p]:mb-[0.85rem]',
    '[&_form>div]:mt-4',
    '[&_form>div:first-of-type]:grid [&_form>div:first-of-type]:grid-cols-2 [&_form>div:first-of-type]:items-start [&_form>div:first-of-type]:gap-x-4 [&_form>div:first-of-type]:gap-y-[1.15rem] [&_form>div:first-of-type]:max-md:grid-cols-1',
    '[&_form_.grid]:grid [&_form_.grid]:grid-cols-2 [&_form_.grid]:items-start [&_form_.grid]:gap-x-4 [&_form_.grid]:gap-y-[1.15rem] [&_form_.grid]:max-md:grid-cols-1',
    '[&_form_label]:mb-1.5 [&_form_label]:block [&_form_label]:text-[0.95rem] [&_form_label]:font-medium [&_form_label]:text-[#021744]',
    "[&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:box-border [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:w-full [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:min-h-12 [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:rounded-[10px] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:border [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:border-gray-300 [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:bg-white [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:px-[0.9rem] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:py-3 [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:text-[#021744] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:outline-none",
    "[&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select):focus]:border-[#22c0b1] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select):focus]:shadow-[0_0_0_3px_rgba(34,192,177,0.16)]",
    '[&_form_textarea]:min-h-[120px] [&_form_textarea]:resize-y',
    "[&_form_input[type='checkbox']]:mr-2 [&_form_input[type='checkbox']]:h-4 [&_form_input[type='checkbox']]:w-4 [&_form_input[type='checkbox']]:flex-shrink-0 [&_form_input[type='checkbox']]:accent-[#22c0b1]",
    "[&_form_input[type='checkbox']+span]:flex [&_form_input[type='checkbox']+span]:items-start [&_form_input[type='checkbox']+span]:gap-1.5 [&_form_input[type='checkbox']+span]:leading-[1.45]",
    "[&_form_label:has(input[type='checkbox'])]:flex [&_form_label:has(input[type='checkbox'])]:items-start [&_form_label:has(input[type='checkbox'])]:gap-1.5 [&_form_label:has(input[type='checkbox'])]:leading-[1.45]",
    '[&_form_.flex.flex-col]:gap-2',
    "[&_form_:is(button,input[type='submit'])]:inline-flex [&_form_:is(button,input[type='submit'])]:min-h-11 [&_form_:is(button,input[type='submit'])]:cursor-pointer [&_form_:is(button,input[type='submit'])]:items-center [&_form_:is(button,input[type='submit'])]:justify-center [&_form_:is(button,input[type='submit'])]:rounded-full [&_form_:is(button,input[type='submit'])]:border-0 [&_form_:is(button,input[type='submit'])]:bg-[#22c0b1] [&_form_:is(button,input[type='submit'])]:px-[1.4rem] [&_form_:is(button,input[type='submit'])]:py-[0.7rem] [&_form_:is(button,input[type='submit'])]:text-base [&_form_:is(button,input[type='submit'])]:font-semibold [&_form_:is(button,input[type='submit'])]:leading-none [&_form_:is(button,input[type='submit'])]:text-white",
    "[&_form_button:hover]:bg-[#021744] [&_form_input[type='submit']:hover]:bg-[#021744]",
  ].join(' ');

function formatBlogDate(dateValue) {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toDateString();
}

function mapBlogRow(blogRes) {
  return {
    postId: blogRes.id || blogRes.Id,
    postType: blogRes.Content_Type || blogRes.ContentType,
    title: blogRes.Title,
    seoTitle: blogRes.SeoTitle,
    metaDescription: blogRes.MetaDescription,
    postDate: blogRes.DateWritten || blogRes.DatePublished || blogRes.LastUpdated,
    postMedia: resolveContentMedia(blogRes, getBlogGraphic2Url),
    postName: blogRes.Relevance,
    creator: blogRes.Author,
    description: blogRes.Description,
    body: blogRes.Body || blogRes.Content || blogRes.content || blogRes.Description,
  };
}

export function BlogDetail() {
  const { blogName } = useParams();
  const [blogData, setBlogData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const siteUrl = getPublicSiteUrl();
  const canonicalUrl = useMemo(() => {
    if (!siteUrl || !blogName) return '';
    return `${siteUrl}/articles/blog/${encodeURIComponent(blogName)}`;
  }, [siteUrl, blogName]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setBlogData(null);
    setLoading(true);
    const name = blogName;
    (async () => {
      if (!name) {
        if (!cancelled) {
          setLoadError('Missing blog');
          setLoading(false);
        }
        return;
      }
      try {
        const blogRes = await getBlogDetail(name);
        if (!cancelled) {
          setBlogData(mapBlogRow(blogRes));
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError('Blog not found');
          setBlogData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blogName]);

  const metaDescription = useMemo(() => {
    if (!blogData) return 'Charlee.ai blogs';
    return metaDescriptionFromRecord({
      metaDescription: blogData.metaDescription,
      description: blogData.description,
      body: blogData.body,
      title: blogData.title,
      fallback: 'Charlee.ai blog',
    });
  }, [blogData]);

  const ogImage = useMemo(() => {
    return getMediaDisplaySrc(blogData?.postMedia);
  }, [blogData]);

  const pageTitle = blogData
    ? documentTitle(blogData.seoTitle, blogData.title, 'Blog')
    : 'Blog | Charlee.ai';
  const publishedDate = formatBlogDate(blogData?.postDate);
  const hasHtmlBody = /<\/?[a-z][\s\S]*>/i.test(blogData?.body || '');

  const breadcrumbItems = useMemo(() => {
    if (!blogData?.title) return undefined;
    return [
      { name: 'Home', path: '/' },
      { name: 'Blogs', path: '/articles/blogs' },
      { name: blogData.title, url: canonicalUrl },
    ];
  }, [blogData, canonicalUrl]);

  return (
    <div className="w-full overflow-hidden bg-[#F3F4F4]">
      {!loading && (
        <DetailPageHelmet
          pageTitle={pageTitle}
          metaDescription={metaDescription}
          canonicalUrl={canonicalUrl}
          ogImage={ogImage}
          ogType="article"
          noIndex={Boolean(loadError)}
          articlePublishedTime={blogData?.postDate}
          author={blogData?.creator}
          breadcrumbItems={breadcrumbItems}
          seoReady={!loading}
        />
      )}

      <HomeHeader headerColor="#F9F9F9" textColor="#021744" />
      <PageMain className="news-page bg-[#F9F9F9]">
        {loading && (
          <div
            className="mt-16 flex min-h-[50vh] w-full items-center justify-center md:mt-24"
            role="status"
            aria-label="Loading"
          >
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#22c0b1] border-t-transparent" />
          </div>
        )}

        {!loading && loadError && (
          <div className="mt-20 w-full px-4 text-center md:mt-32">
            <p className="text-lg text-[#021744]">{loadError}</p>
            <Link to="/articles/blogs" className="mt-4 inline-block text-[#22c0b1] underline">
              Back to blogs
            </Link>
          </div>
        )}

        {!loading && blogData && (
          <>
            <article className="mt-16 w-full md:mt-24">
              <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">
                <div className="w-full pt-6 md:pt-14">
                  <h1 className="text-[28px] font-medium leading-[1.15] tracking-normal sm:text-[36px] sm:leading-[39.6px] md:text-[65px] md:leading-[71.5px]">
                    {blogData.title}
                  </h1>
                  <div className="flex w-full flex-wrap items-center justify-center gap-y-1 py-3 text-center text-sm text-[#0099B0] sm:gap-0 sm:py-2 sm:text-base">
                    <span className="px-2 sm:px-3">3 minute read</span>
                    {publishedDate ? (
                      <span className="border-l border-l-[#0099B0] px-2 sm:px-3">{publishedDate}</span>
                    ) : null}
                    <span className="border-l border-l-[#0099B0] px-2 sm:px-3">By Charlee AI</span>
                  </div>
                  <div className="w-full rounded-xl py-6 md:py-10">
                    <div className="w-full space-y-6 text-left md:space-y-8">
                      {ogImage ? (
                        <div className="w-full min-h-[220px] rounded-xl sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]">
                          <img
                            src={ogImage}
                            alt={blogData.title ? `${blogData.title} — Charlee.ai blog` : 'Charlee.ai blog'}
                            className="h-full min-h-[220px] w-full rounded-xl object-cover sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
                            crossOrigin="anonymous"
                          />
                        </div>
                      ) : null}
                      {hasHtmlBody ? (
                        <div
                          className={BLOG_RICH_HTML_CLASS}
                          dangerouslySetInnerHTML={{ __html: blogData.body }}
                        />
                      ) : (
                        <p className="new-blog-content mt-4 whitespace-pre-line text-base leading-7 text-[#021744] md:text-[18px] md:leading-[30px]">
                          {blogData.body}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </article>
            <div className="w-full bg-[#F3F4F4] py-12 md:py-20">
              <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
                <div className="flex w-full justify-start md:justify-end">
                  <Link to="/articles/blogs" className="w-full sm:w-auto">
                    <button
                      type="button"
                      className="w-full rounded-full border border-[#021744] px-6 py-3 text-base font-medium leading-6 hover:bg-[#021744] hover:text-white sm:w-auto md:px-8 md:py-4 md:text-[18px]"
                    >
                      View all Blogs
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </PageMain>
      <Footer />
    </div>
  );
}
